import restClient from "api/restClient";
import { Button } from "components/Button/Button";
import { Modal } from "components/Modal/Modal";
import NotificationService from "components/Notification/Notification.service";
import RestErrorNotification from "components/Notification/RestErrorNotification";
import SimpleNotification from "components/Notification/SimpleNotification";
import { DragAndDropCard } from "containers/DragAndDrop/DragAndDropCard";
import { useDragAndDropList } from "containers/DragAndDrop/useDragAndDropList";
import { useWorkspaceConfigValue } from "containers/WorkspaceConfig/useWorkspaceConfigValue";
import { useCallback, useEffect, useState } from "react";
import "./AttributeReorderModal.scss";

interface ReorderAttribute {
    id: string;
    name: string;
    sort_order?: number | null;
}

interface AttributeReorderModalProps {
    isOpen: boolean;
    onClose: () => void;
    objectTemplateId: string;
    /** Called after a successful reorder so the caller can refetch the table. */
    onSaved: () => void;
}

// Unique react-dnd item type, scoped to this modal so its drag targets never
// interfere with the object-template canvas' own attribute drag.
const DRAG_TYPE = "attribute_reorder_modal";

/**
 * Modal that lets the user drag every attribute of an object template into a new
 * order and persist the whole order in a SINGLE backend call
 * (`POST /object_template/{id}/reorder_attributes`), producing one transaction
 * and one git-sync commit. It fetches the FULL attribute list itself (the
 * attributes table on the screen is paginated) because the endpoint requires an
 * exact permutation of all the template's attribute ids.
 * See operator-task-fe-ot-attribute-reorder-modal-batch-save.
 */
export const AttributeReorderModal = ({
    isOpen,
    onClose,
    objectTemplateId,
    onSaved,
}: AttributeReorderModalProps) => {
    const canCommit = useWorkspaceConfigValue("GIT_AUTOCOMMIT_ENABLED") === true;
    const [attributes, setAttributes] = useState<ReorderAttribute[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const { cards, onMoveCard } = useDragAndDropList(attributes);

    // Fetch the full ordered attribute list whenever the modal opens, so the
    // reorder operates on every attribute regardless of the table's page size.
    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }
        let cancelled = false;
        setIsLoading(true);
        setLoadError(false);
        const filter = JSON.stringify({
            field: "object_template_id",
            op: "eq",
            value: objectTemplateId,
        });
        restClient
            .get("/object_template_attribute", {
                // plain=true skips the total-count query; the list arrives as a bare array.
                params: { filter, order: "+sort_order", limit: 1000, plain: true },
            })
            .then((response) => {
                if (cancelled) {
                    return;
                }
                const list: ReorderAttribute[] = Array.isArray(response?.data)
                    ? response.data
                    : [];
                setAttributes(
                    list.map((attribute) => ({
                        id: attribute.id,
                        name: attribute.name,
                        sort_order: attribute.sort_order,
                    }))
                );
            })
            .catch((error) => {
                if (cancelled) {
                    return;
                }
                setLoadError(true);
                NotificationService.error(
                    <RestErrorNotification
                        titleOverride="Could not load attributes"
                        error={error}
                    />
                );
            })
            .finally(() => {
                if (!cancelled) {
                    setIsLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [isOpen, objectTemplateId]);

    const isOrderChanged = cards.some((card, index) => attributes[index]?.id !== card.id);

    const handleSave = useCallback(() => {
        if (!cards.length) {
            return;
        }
        setIsSaving(true);
        restClient
            .post(`/object_template/${objectTemplateId}/reorder_attributes?commit=${canCommit}`, {
                attribute_ids: cards.map((card) => card.id),
            })
            .then(() => {
                NotificationService.success(
                    <SimpleNotification
                        title="Attributes reordered"
                        subtitle="The new attribute order was saved."
                    />
                );
                onSaved();
                onClose();
            })
            .catch((error) => {
                NotificationService.error(
                    <RestErrorNotification
                        titleOverride="Could not reorder attributes"
                        error={error}
                    />
                );
            })
            .finally(() => {
                setIsSaving(false);
            });
    }, [cards, objectTemplateId, canCommit, onSaved, onClose]);

    return (
        <Modal
            isVisible={isOpen}
            onCancel={onClose}
            title="Re-order attributes"
            width="480px"
            closable={false}
            destroyOnClose
        >
            <div className="attributeReorderModal">
                {isLoading ? (
                    <div className="attributeReorderModal__message">Loading attributes…</div>
                ) : loadError ? (
                    <div className="attributeReorderModal__message">
                        Could not load attributes. Close the dialog and try again.
                    </div>
                ) : cards.length === 0 ? (
                    <div className="attributeReorderModal__message">No attributes to re-order.</div>
                ) : (
                    <>
                        <p className="attributeReorderModal__hint">
                            Drag the attributes into the desired order, then press Save. The whole
                            order is saved in a single step.
                        </p>
                        <ul className="attributeReorderModal__list">
                            {cards.map((attribute, index) => (
                                <li key={attribute.id}>
                                    <DragAndDropCard
                                        id={attribute.id}
                                        index={index}
                                        onMoveCard={onMoveCard}
                                        applyAction={() => undefined}
                                        type={DRAG_TYPE}
                                        canDrag={cards.length > 1}
                                    >
                                        <span className="attributeReorderModal__name">
                                            {attribute.name}
                                        </span>
                                    </DragAndDropCard>
                                </li>
                            ))}
                        </ul>
                    </>
                )}
                {/*
                  Canonical modal button row (ui-design-principle-modals-dialogs
                  reference impl = LocationPickerPopover): Cancel on the LEFT as
                  the neutral white/secondary button, primary-blue confirm on the
                  RIGHT. No corner close "×" (closable={false} above).
                */}
                <div className="attributeReorderModal__actions">
                    <Button
                        label="Cancel"
                        variation="white"
                        onClick={onClose}
                        disabled={isSaving}
                    />
                    <Button
                        label="Save"
                        variation="primary"
                        onClick={handleSave}
                        loading={isSaving}
                        disabled={isSaving || isLoading || !cards.length || !isOrderChanged}
                    />
                </div>
            </div>
        </Modal>
    );
};
